import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, Select, DatePicker, Input, AutoComplete } from 'antd';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { API } from '@services/api.service';
import { OPERATION } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { CURRENCY_OPTIONS, QUERY_KEY } from '@/shared/constants';
import { useSalaryStore } from '@/store/useSalaryStore';
import {
  DATE_FORMATS,
  formatDateForBackend,
  isEmployeeRequestFormDisabled,
} from '@/utils/formUtils';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import { translateOptions } from '@/utils/utils';
import CustomDatePicker from '../input/CustomDatePicker';
import FormActionButton from '../input/FormActionButton';

export default function SuspensionForm({
  formData,
  typeOfLeave,
  employeeId,
  handleRefetch,
}) {
  const { t } = useTranslation(['menus', 'forms', 'messages']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const notification = useNotify();

  const { toggle, setToggle } = useSalaryStore(
    useShallow((state) => ({
      toggle: state.toggle,
      setToggle: state.setToggle,
    }))
  );

  const handleCancelModal = () => {
    if (toggle.leaveExtender) {
      setToggle('leaveExtender', false);

      // * Refetch after submitted
      handleRefetch();
    }
  };

  const currencyOptions = useMemo(() => {
    return translateOptions(CURRENCY_OPTIONS, t);
  }, [t]);

  // Check if company has suspension variations enabled
  const hasSuspensionVariations = useMemo(() => {
    return (
      formData?.company?.suspensionVariations?.enabled &&
      formData?.company?.suspensionVariations?.types?.length > 0
    );
  }, [formData?.company?.suspensionVariations]);

  // Get suspension variation type options (with "None" option)
  const suspensionVariationOptions = useMemo(() => {
    if (!hasSuspensionVariations) return [];
    const options = formData.company.suspensionVariations.types.map((type) => ({
      value: type.key,
      label: type.label,
    }));
    // Add "None" option at the end
    options.push({
      value: 'none',
      label: t('none', { ns: 'forms' }),
    });
    return options;
  }, [
    hasSuspensionVariations,
    formData?.company?.suspensionVariations?.types,
    t,
  ]);

  // Track if "None" is selected for suspension variation
  const [isNoneSelected, setIsNoneSelected] = useState(false);

  const isCompanyBilingual =
    formData?.company?.applicationSettings?.typeOfHr?.key === 'bilingual';

  const suspensionReasonOptionsSer = [
    { value: 'Kredit' },
    { value: 'Mobilni telefon' },
    { value: 'Privatno zdravstveno osiguranje' },
  ];

  const suspensionReasonOptionsEng = [
    { value: 'Credit' },
    { value: 'Mobile phone' },
    { value: 'Private health insurance' },
  ];

  // * Form instance
  const [form] = Form.useForm();

  // * Initial form data
  const initialValues = {
    dateOfLeave: [],
    dateOfRequestCreate: null,
    currencySuspension:
      formData?.company?.otherAllowances?.suspension?.label || 'RSD',
  };

  // * Enabled fetch data if ok
  const isEnabled = (queryParams, employeeId) => {
    const { action, requestId } = queryParams;
    const hasAction = [OPERATION.view, OPERATION.edit].includes(action);
    return !!requestId && !!employeeId && !!hasAction;
  };

  // * Fetch data for (view or update)
  const { data: requestData } = useQuery({
    queryKey: [
      QUERY_KEY.request,
      employeeId,
      queryParams.action,
      queryParams.requestId,
    ],
    queryFn: async () => {
      return await API.requests.getRequestById(queryParams.requestId);
    },
    enabled: isEnabled(queryParams, employeeId),
    retry: false,
  });

  // * Show submitted action and disable input fields if not `ACCEPTED` or not `edit` mode
  const hasSubmitted = isEmployeeRequestFormDisabled(
    queryParams?.action ?? '',
    requestData
  );

  // * Set `initial data` from (update or view)
  useEffect(() => {
    if (requestData) {
      const {
        reasonOfLeave,
        suspendPaymentMonth,
        valueSuspension,
        currencySuspension,
        dateOfCreation,
        suspensionVariationType,
      } = requestData;

      // Check if it's "none" type or has no variation type but has reasonOfLeave
      const isNone =
        suspensionVariationType === 'none' ||
        (!suspensionVariationType && reasonOfLeave);
      setIsNoneSelected(isNone);

      form.setFieldsValue({
        reasonOfLeaveText: reasonOfLeave,
        suspendPaymentMonth: suspendPaymentMonth
          ? dayjs(suspendPaymentMonth)
          : null,
        valueSuspension: valueSuspension,
        currencySuspension: currencySuspension,
        dateOfRequestCreate: dateOfCreation ? dayjs(dateOfCreation) : null,
        suspensionVariationType: isNone
          ? 'none'
          : suspensionVariationType || undefined,
      });
    }
  }, [form, requestData]);

  // * Handle reset form
  const onReset = useCallback(() => {
    form.resetFields();
  }, [form]);

  // * After submitted form (create or edit) redirect to `route=hr-documents`
  const goToHrDocuments = useCallback(() => {
    setQueryParams({ ['route']: 'hr-documents', ['action']: undefined });
  }, [setQueryParams]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (queryParams.action === 'edit') {
        return await API.requests.updateRequestById(
          queryParams.requestId,
          payload
        );
      }

      return await API.requests.createNewRequest(payload);
    },
    onSuccess: (response) => {
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });

      // * Close modal if open your `LeaveExtenderModal` form modal
      handleCancelModal();

      // * Reset form fields
      onReset();

      // * Redirect to `route=hr-documents` after submitted form
      goToHrDocuments();
    },
    onError: (error) => {
      const errorMsg = error.response.data.error || error.message;

      notification.error({
        message: t('error', { ns: 'messages' }),
        description: errorMsg,
      });
    },
  });

  const onFinish = async (values) => {
    const { _id: employeeId, company, firstName, lastName } = formData;
    const {
      dateOfRequestCreate,
      currencySuspension,
      suspendPaymentMonth,
      reasonOfLeaveText,
      reasonOfLeaveTextEng,
      valueSuspension,
      suspensionVariationType,
    } = values;

    const payload = {
      employee: employeeId,
      company: company._id,
      firstName,
      lastName,
      typeOfLeave,
      currencySuspension: currencySuspension.toLowerCase(),
      valueSuspension: valueSuspension,
      reasonOfLeave: reasonOfLeaveText,
      reasonOfLeaveEng: reasonOfLeaveTextEng,
      suspensionMonth: formatDateForBackend(
        suspendPaymentMonth,
        DATE_FORMATS.STANDARD
      ),
      dateOfCreation: formatDateForBackend(
        dateOfRequestCreate,
        DATE_FORMATS.STANDARD
      ),
    };

    // Add suspension variation if company has variations enabled and a type was selected (not "none")
    if (
      hasSuspensionVariations &&
      suspensionVariationType &&
      suspensionVariationType !== 'none'
    ) {
      const selectedType = formData.company.suspensionVariations.types.find(
        (t) => t.key === suspensionVariationType
      );
      payload.suspensionVariationType = suspensionVariationType;
      payload.suspensionVariationLabel = selectedType?.label || '';
    }

    if (!hasSubmitted) {
      await mutateAsync(payload);
    } else {
      console.warn('Please check your hasSubmitted value', hasSubmitted);
    }
  };

  return (
    <Form
      form={form}
      name="employee_request-suspension-form"
      initialValues={initialValues}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className={cn('form grid gap-4')}
    >
      <div className="rounded-md border bg-zinc-100 p-4">
        {hasSuspensionVariations && (
          <>
            <Form.Item
              label={t('suspensionVariationType', { ns: 'forms' })}
              name="suspensionVariationType"
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <Select
                placeholder={t('selectSuspensionVariationType', {
                  ns: 'forms',
                })}
                className="w-full"
                disabled={hasSubmitted}
                options={suspensionVariationOptions}
                onChange={(value) => setIsNoneSelected(value === 'none')}
              />
            </Form.Item>

            <hr className="mb-5" />
          </>
        )}

        {(!hasSuspensionVariations || isNoneSelected) && (
          <>
            <Form.Item
              label={t('reasonOfSuspension', { ns: 'forms' })}
              name="reasonOfLeaveText"
              rules={[
                {
                  required: !hasSuspensionVariations || isNoneSelected,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <AutoComplete
                options={suspensionReasonOptionsSer}
                placeholder={t('reasonOfSuspension', { ns: 'forms' })}
                disabled={hasSubmitted}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>

            <hr className="mb-5" />
          </>
        )}

        {(!hasSuspensionVariations || isNoneSelected) && isCompanyBilingual && (
          <>
            <Form.Item
              label={t('reasonOfSuspensionEng', { ns: 'forms' })}
              name="reasonOfLeaveTextEng"
              rules={[
                {
                  required: !hasSuspensionVariations || isNoneSelected,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <AutoComplete
                options={suspensionReasonOptionsEng}
                placeholder={t('reasonOfSuspensionEng', { ns: 'forms' })}
                disabled={hasSubmitted}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>

            <hr className="mb-5" />
          </>
        )}

        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item
            label={t('monthSuspendPayment', { ns: 'forms' })}
            name="suspendPaymentMonth"
            className="whitespace-nowrap"
            rules={[
              {
                required: true,
                message: t('formValidation.selectDate', { ns: 'forms' }),
              },
            ]}
          >
            <DatePicker
              picker="month"
              className="w-full"
              format={DATE_FORMATS.MONTH_YEAR}
              disabled={hasSubmitted}
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              label={t('value', { ns: 'forms' })}
              name="valueSuspension"
              className="whitespace-nowrap"
              rules={[
                {
                  required: true,
                  message: t('formValidation.numberField.invalid', {
                    ns: 'forms',
                  }),
                },
              ]}
            >
              <Input
                min={0}
                type="number"
                step="any"
                placeholder={t('value', { ns: 'forms' })}
                disabled={hasSubmitted}
                onChange={(e) =>
                  formValidateAndSetNumber(form, e, 'valueSuspension')
                }
              />
            </Form.Item>

            <Form.Item
              name="currencySuspension"
              label={t('currency', { ns: 'forms' })}
              rules={[
                {
                  required: true,
                  message: t('formValidation.required', { ns: 'forms' }),
                },
              ]}
            >
              <Select
                mode="single"
                placeholder={t('currency', { ns: 'forms' })}
                className="w-full"
                loading={false}
                disabled={hasSubmitted}
                options={currencyOptions}
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item
          label={t('dateOfRequestCreation', { ns: 'forms' })}
          name="dateOfRequestCreate"
        >
          <CustomDatePicker form={form} disabled={hasSubmitted} />
        </Form.Item>
      </div>

      {!hasSubmitted && (
        <FormActionButton isPending={isPending} onReset={onReset} />
      )}
    </Form>
  );
}
